'use client';

import { useEffect, useState } from 'react';
import { useProfileData } from '../queries/useProfileQueries';

/**
 * Two switches, two separate audiences. They never combine — mixing them is what
 * this file exists to prevent.
 *
 * - The OS switch (`prefers-reduced-motion`) is about movement in general and
 *   governs the interface: press feedback, travelling indicators, sliding bars.
 * - The profile switch (`animationsEnabled`) is the setting labelled «Анимация
 *   рулетки» / «Case roulette animation». It governs the game reels — the case
 *   roulette spin, the honeycomb burn, the upgrade dial, the contract crate —
 *   and nothing else. Someone turns it off to open a case instantly.
 *
 * Neither switch reaches across: the OS preference does not shorten a spin, and
 * the profile setting does not freeze the tab bar.
 */

/** Raw OS preference. `true` means the person asked for less movement. */
export function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(mq.matches);
    const onChange = () => setReduced(mq.matches);
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);

  return reduced;
}

/**
 * Interface motion: press feedback, travelling indicators, sliding bars, list
 * cross-fades. Gated by the OS preference ONLY — the profile setting is about
 * the roulette and has no say here.
 */
export function useUiMotionEnabled(): boolean {
  return !usePrefersReducedMotion();
}

/**
 * Game motion: the case roulette spin, the honeycomb burn, the upgrade dial, the
 * contract crate. Driven by the profile setting ALONE. The OS preference is
 * deliberately not consulted — «Анимация рулетки» is the only switch that speaks
 * for the reels, and a phone-wide preference must not silently override it.
 */
export function useGameMotionEnabled(): boolean {
  const profile = useProfileData();

  return profile?.animationsEnabled ?? true;
}
